import { PerformanceMetric, usePerformance } from '@/context/performance';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useRef, useState } from 'react';
import { AppBundle, AppBundleRoutes, getEntrypoint, getImportShim, registerPreloadPromise } from '../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import { sentryAddAppLoadBreadcrumb, sentryCaptureAppLoadDiagnostic } from '@/utils/sentry';
import {
  plumaColorSchemePreferenceStore,
  plumaColorSchemeStore,
  plumaContextValueStore,
  plumaThemeStore,
  plumaThemesStore,
} from '@/components/connected-pluma-provider';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';

// Types for dynamically loaded Journeys app
type JourneysAppInstance = {
  injectScripts: () => Promise<void>;
  create: () => void;
  destroy: () => void;
};

type JourneysAppConstructor = new (
  element: HTMLElement,
  options?: {
    hydraProps?: {
      scrollContainerSelector?: string;
      useNewWorkspacesUrls?: boolean;
      journeysTaxonomyRename?: boolean;
      plumaContextValue?: typeof plumaContextValueStore;
      plumaTheme?: typeof plumaThemeStore;
      plumaColorScheme?: typeof plumaColorSchemeStore;
      plumaColorSchemePreference?: typeof plumaColorSchemePreferenceStore;
      plumaThemes?: typeof plumaThemesStore;
    };
  },
) => JourneysAppInstance;

type JourneysModule = {
  App: JourneysAppConstructor;
  default?: unknown;
};

export default function Journeys() {
  return <JourneysBackgroundApp />;
}

export function JourneysNonBackgroundApp() {
  const ref = useRef(null);
  const appInstance = useRef<JourneysAppInstance | null>(null);
  const [loaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const recordPerformanceMetric = usePerformance();

  if (loadError != null) {
    throw loadError;
  }

  useMountEffect(() => {
    setChildAppLoaded(false);
  });

  useMountEffect(() => {
    const load = async () => {
      recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
      sentryAddAppLoadBreadcrumb('journeys non-background entrypoint requested', { appBundle: AppBundle.Journeys });
      const entrypoint = (await getEntrypoint(AppBundle.Journeys)) as JourneysModule;
      sentryAddAppLoadBreadcrumb('journeys non-background entrypoint resolved', { appBundle: AppBundle.Journeys });
      if (appInstance.current == null && ref.current != null) {
        // Read at boot time, not render time: the embedded Ember router consumes this
        // once when it builds its route map. The authenticated layout gates on the
        // account being loaded, so the per-account flag is populated by now.
        const journeysTaxonomyRename = hasFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRename);
        appInstance.current = new entrypoint.App(ref.current, {
          hydraProps: {
            scrollContainerSelector: '#embedded-app-container',
            useNewWorkspacesUrls: true,
            journeysTaxonomyRename,
            plumaContextValue: plumaContextValueStore,
            plumaTheme: plumaThemeStore,
            plumaColorScheme: plumaColorSchemeStore,
            plumaColorSchemePreference: plumaColorSchemePreferenceStore,
            plumaThemes: plumaThemesStore,
          },
        });

        sentryAddAppLoadBreadcrumb('journeys non-background inject scripts started', { appBundle: AppBundle.Journeys });
        await appInstance.current.injectScripts();
        sentryAddAppLoadBreadcrumb('journeys non-background inject scripts resolved', {
          appBundle: AppBundle.Journeys,
        });
        setIsLoaded(true);
        sentryAddAppLoadBreadcrumb('journeys non-background create called', { appBundle: AppBundle.Journeys });
        appInstance.current.create();
      }
    };

    load().catch((error: unknown) => {
      sentryCaptureAppLoadDiagnostic(
        'journeys_non_background_load_rejected',
        {
          appBundle: AppBundle.Journeys,
          errorName: error instanceof Error ? error.name : typeof error,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
        { level: 'error' },
      );
      setLoadError(error instanceof Error ? error : new Error('Failed to load Journeys.'));
    });

    return () => {
      appInstance.current?.destroy();
      appInstance.current = null;
      setIsLoaded(false);
    };
  });

  performance.mark('hydra:journeys-non-background-app:render');

  return (
    <>
      <div
        className="journeys-app"
        ref={ref}
        style={{ display: loaded ? 'flex' : 'none', minWidth: 0, flex: 1, height: 'fit-content' }}
      />
      {loaded ? null : <PageLoader />}
    </>
  );
}

function JourneysBackgroundApp() {
  const renderJourneysApp = async (appWrapper: HTMLDivElement) => {
    sentryAddAppLoadBreadcrumb('journeys background entrypoint requested', { appBundle: AppBundle.Journeys });
    const entrypoint = (await getEntrypoint(AppBundle.Journeys)) as JourneysModule;
    sentryAddAppLoadBreadcrumb('journeys background entrypoint resolved', { appBundle: AppBundle.Journeys });

    // Read at boot time, not render time: the embedded Ember router consumes this once
    // when it builds its route map. The authenticated layout gates on the account being
    // loaded, so the per-account flag is populated by now.
    const journeysTaxonomyRename = hasFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRename);
    const appInstance = new entrypoint.App(appWrapper, {
      hydraProps: {
        scrollContainerSelector: '#embedded-app-container',
        useNewWorkspacesUrls: true,
        journeysTaxonomyRename,
        plumaContextValue: plumaContextValueStore,
        plumaTheme: plumaThemeStore,
        plumaColorScheme: plumaColorSchemeStore,
        plumaColorSchemePreference: plumaColorSchemePreferenceStore,
        plumaThemes: plumaThemesStore,
      },
    });

    sentryAddAppLoadBreadcrumb('journeys background inject scripts started', { appBundle: AppBundle.Journeys });
    await appInstance.injectScripts();
    sentryAddAppLoadBreadcrumb('journeys background inject scripts resolved', { appBundle: AppBundle.Journeys });
    sentryAddAppLoadBreadcrumb('journeys background create called', { appBundle: AppBundle.Journeys });
    appInstance.create();

    return () => {
      appInstance.destroy();
    };
  };

  performance.mark('hydra:journeys-background-app:render');

  return (
    <AppBundleLoader
      appBundle={AppBundle.Journeys}
      className="journeys-app"
      style="height: fit-content;"
      renderApp={renderJourneysApp}
    />
  );
}

export const preloadJourneysAppBundle = () => {
  // If the URL contains other app bundles, don't preload Journeys
  // (doing by exclusion, rather than checking for the presence of Journeys, because Journeys is the default app bundle)
  const url = String(window.location.href);
  const otherAppBundles = Object.values(AppBundleRoutes).filter(
    (route) => route !== AppBundleRoutes[AppBundle.Journeys],
  );
  if (otherAppBundles.some((bundle) => url.includes(bundle))) {
    return;
  }

  const preloadJourneysApp = async () => {
    performance.mark('hydra:journeys-preload:init');
    sentryAddAppLoadBreadcrumb('journeys preload entrypoint requested', { appBundle: AppBundle.Journeys });
    const entrypoint = (await getImportShim()(AppBundle.Journeys)) as JourneysModule;
    sentryAddAppLoadBreadcrumb('journeys preload entrypoint resolved', { appBundle: AppBundle.Journeys });

    performance.mark('hydra:journeys-preload:importshim-entrypoint-ready');

    // The App constructor requires an element to be passed, but it's not
    // actually used if we don't call `create()`
    const dummyAppWrapper = document.createElement('div');
    const appInstance = new entrypoint.App(dummyAppWrapper);
    performance.mark('hydra:journeys-preload:inject-scripts');
    sentryAddAppLoadBreadcrumb('journeys preload inject scripts started', { appBundle: AppBundle.Journeys });
    await appInstance.injectScripts();
    sentryAddAppLoadBreadcrumb('journeys preload inject scripts resolved', { appBundle: AppBundle.Journeys });
    performance.mark('hydra:journeys-preload:inject-scripts-complete');
    dummyAppWrapper.remove();
    appInstance.destroy();
    return entrypoint;
  };

  registerPreloadPromise(AppBundle.Journeys, preloadJourneysApp);
};
